/**
 * Hooks for the persistent system-notifications inbox (bell popover).
 *
 * The bell shows non-messaging notifications only — the AppBar MessageSquare
 * icon handles messaging unread count separately.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../api/axiosInstance';

const API_BASE = '/api/v1/notifications/inbox';

export interface InboxNotification {
  id: string;
  notification_type: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  data: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export const notificationsInboxKeys = {
  all: ['notifications-inbox'] as const,
  // The limit is part of the key: the bell widens its page while the reader is
  // filtering, and two callers asking for different page sizes must not share
  // one cache entry. Every invalidation targets the `all` prefix, so it still
  // sweeps both.
  list: (limit: number) => [...notificationsInboxKeys.all, 'list', limit] as const,
  search: (limit: number, search: InboxSearch | null) =>
    [...notificationsInboxKeys.all, 'search', limit, search] as const,
  unreadCount: () => [...notificationsInboxKeys.all, 'unread-count'] as const,
};

/** What the reader typed, plus what only the client could resolve from it. */
export interface InboxSearch {
  /** The raw query, matched server-side against the stored title and body. */
  query: string;
  /**
   * Notification types whose LOCALIZED catalog title matched the same query —
   * see `notificationTypesMatchingQuery`. The stored title is English and the
   * bell renders the catalog label, so without these a reader searching their
   * own language (or a tenant's renamed terminology) matches nothing.
   */
  types: string[];
}

/** One page of the inbox, plus how many rows the tenant actually has. */
export interface InboxPage {
  items: InboxNotification[];
  /**
   * Rows matching the request's filters, NOT the number returned.
   *
   * Under a search that is the MATCH count, so the gap to `items.length` says
   * the reader's match set was truncated — never that their inbox was. Without
   * it the bell cannot tell "nothing matches" from "nothing matches in the part
   * I have", and saying the first when the second is true is a lie the reader
   * cannot detect.
   */
  total: number;
}

export function useInboxNotifications(limit = 30) {
  return useQuery({
    queryKey: notificationsInboxKeys.list(limit),
    queryFn: async (): Promise<InboxPage> => {
      const { data } = await axiosInstance.get<InboxPage>(API_BASE, {
        params: { limit },
      });
      return { items: data.items, total: data.total };
    },
    // Keep the rows on screen while a widened page loads, so typing into the
    // bell's filter never blanks the list it is filtering.
    placeholderData: keepPreviousData,
  });
}

/**
 * Notifications MATCHING a query, from anywhere in the reader's inbox.
 *
 * The sibling above returns the newest N rows; this one asks the server to
 * search. The bell is the only global notifications surface, so a filter that
 * could reach no further than the page it had loaded reported a real
 * notification as absent — which the reader has no way to detect.
 *
 * Disabled for an empty query, so a resting bell issues no request of its own.
 * `keepPreviousData` holds the previous query's matches on screen while the next
 * keystroke's page arrives; the client's filter drops any that no longer match.
 */
export function useInboxNotificationSearch(search: InboxSearch | null, limit = 100) {
  return useQuery({
    queryKey: notificationsInboxKeys.search(limit, search),
    queryFn: async (): Promise<InboxPage> => {
      const { data } = await axiosInstance.get<InboxPage>(API_BASE, {
        params: { limit, search: search?.query, search_types: search?.types },
        // Repeated `search_types=` params, which is what FastAPI reads a
        // `list[str]` query parameter from. Axios' default bracket form
        // (`search_types[]=`) reaches the route as no value at all.
        paramsSerializer: { indexes: null },
      });
      return { items: data.items, total: data.total };
    },
    enabled: Boolean(search?.query),
    placeholderData: keepPreviousData,
  });
}

export function useInboxUnreadCount() {
  return useQuery({
    queryKey: notificationsInboxKeys.unreadCount(),
    queryFn: async (): Promise<number> => {
      const { data } = await axiosInstance.get<{ total_unread: number }>(
        `${API_BASE}/unread-count`,
      );
      return data.total_unread;
    },
    refetchInterval: 60000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string): Promise<void> => {
      await axiosInstance.post(`${API_BASE}/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsInboxKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      const { data } = await axiosInstance.post<{ updated: number }>(
        `${API_BASE}/mark-all-read`,
      );
      return data.updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsInboxKeys.all });
    },
  });
}
